import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';

interface VisualExampleProps {
  title: string;
  caption?: string;
  children: React.ReactNode;
}

export const VisualExample: React.FC<VisualExampleProps> = ({
  title,
  caption,
  children,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: 2,
        borderColor: 'primary.light',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 3,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 3, bgcolor: 'grey.50' }}>{children}</Box>
      {caption && (
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {caption}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

// Mini grid component for visual examples
interface MiniGridProps {
  rows?: number;
  cols?: number;
  highlighted?: { row: number; col: number; color?: string; label?: string }[];
  homeNumbers?: number[];
  awayNumbers?: number[];
  showNumbers?: boolean;
  homeTeam?: string;
  awayTeam?: string;
}

export const MiniGrid: React.FC<MiniGridProps> = ({
  rows = 5,
  cols = 5,
  highlighted = [],
  homeNumbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  awayNumbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  showNumbers = true,
  homeTeam = 'Home',
  awayTeam = 'Away',
}) => {
  const getHighlight = (row: number, col: number) =>
    highlighted.find((h) => h.row === row && h.col === col);

  return (
    <Box sx={{ display: 'inline-block' }}>
      <Box sx={{ display: 'flex' }}>
        {/* Empty corner */}
        <Box sx={{ width: 60, height: 32 }} />
        {/* Home team header */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
            fontSize: 12,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
        >
          {homeTeam}
        </Box>
      </Box>
      <Box sx={{ display: 'flex' }}>
        {/* Away team label */}
        <Box
          sx={{
            width: 32,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'error.main',
            color: 'white',
            fontWeight: 'bold',
            fontSize: 12,
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
          }}
        >
          {awayTeam}
        </Box>
        {/* Number column for away */}
        {showNumbers && (
          <Box sx={{ width: 28 }}>
            {/* Spacer to align with home numbers row */}
            <Box sx={{ height: 28 }} />
            {Array.from({ length: rows }, (_, i) => (
              <Box
                key={i}
                sx={{
                  width: 28,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.200',
                  borderBottom: 1,
                  borderColor: 'grey.300',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {awayNumbers[i]}
              </Box>
            ))}
          </Box>
        )}
        {/* Grid */}
        <Box>
          {/* Number row for home */}
          {showNumbers && (
            <Box sx={{ display: 'flex' }}>
              {Array.from({ length: cols }, (_, j) => (
                <Box
                  key={j}
                  sx={{
                    width: 36,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.200',
                    borderRight: 1,
                    borderColor: 'grey.300',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {homeNumbers[j]}
                </Box>
              ))}
            </Box>
          )}
          {/* Squares */}
          {Array.from({ length: rows }, (_, i) => (
            <Box key={i} sx={{ display: 'flex' }}>
              {Array.from({ length: cols }, (_, j) => {
                const highlight = getHighlight(i, j);
                return (
                  <Box
                    key={j}
                    sx={{
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: highlight?.color || 'white',
                      border: 1,
                      borderColor: 'grey.300',
                      fontSize: 10,
                      fontWeight: highlight ? 600 : 400,
                      color: highlight?.color === 'success.main' || highlight?.color === 'error.main' ? 'white' : 'text.secondary',
                    }}
                  >
                    {highlight?.label || ''}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Step indicator for process flows
interface StepIndicatorProps {
  steps: { title: string; description: string }[];
  currentStep?: number;
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {steps.map((step, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: currentStep === index ? 'primary.main' : currentStep !== undefined && index < currentStep ? 'success.main' : 'grey.300',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            {index + 1}
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              {step.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step.description}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Formula display for calculations
interface FormulaProps {
  formula: string;
  result?: string;
  variables?: { name: string; value: string; description: string }[];
}

export const Formula: React.FC<FormulaProps> = ({ formula, result, variables }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        bgcolor: 'grey.100',
        borderRadius: 2,
        fontFamily: 'monospace',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {formula}
        </Typography>
        {result && (
          <>
            <Typography variant="body1">=</Typography>
            <Chip label={result} color="primary" size="small" />
          </>
        )}
      </Box>
      {variables && variables.length > 0 && (
        <Box sx={{ mt: 2, pl: 2, borderLeft: 2, borderColor: 'primary.main' }}>
          {variables.map((v) => (
            <Typography key={v.name} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>{v.name}</strong> = {v.value} ({v.description})
            </Typography>
          ))}
        </Box>
      )}
    </Paper>
  );
};

// Money display component
interface MoneyDisplayProps {
  label: string;
  amount: number;
  variant?: 'positive' | 'negative' | 'neutral';
  size?: 'small' | 'medium' | 'large';
}

export const MoneyDisplay: React.FC<MoneyDisplayProps> = ({
  label,
  amount,
  variant = 'neutral',
  size = 'medium',
}) => {
  const colors = {
    positive: 'success.main',
    negative: 'error.main',
    neutral: 'text.primary',
  };

  const sizes = {
    small: { label: 'caption', amount: 'body2' },
    medium: { label: 'body2', amount: 'h6' },
    large: { label: 'body1', amount: 'h4' },
  };

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant={sizes[size].label as 'caption' | 'body2' | 'body1'} color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant={sizes[size].amount as 'body2' | 'h6' | 'h4'}
        color={colors[variant]}
        fontWeight="bold"
      >
        ${amount.toFixed(2)}
      </Typography>
    </Box>
  );
};

export default VisualExample;
